import { bobLanding, bobFinishBonus } from './72-bob-mastery.mjs';
import { getBobLevel } from './67-bob-levels.mjs';
import { bobWorldForces } from './73-bob-worlds.mjs';
import {
  BOB_ARCADE,
  createBobArcade,
  resetBobArcade,
  rewardBobArcade,
  bobReactionFood,
} from './91-bob-arcade.mjs';

export const BOB_PHYSICS = Object.freeze({
  stepMs: 16,
  tapCooldownMs: 180,
  stumbleGraceMs: 1200,
  gravity: 11.5,
  radius: 0.55,
  maxSpeed: 12.5,
});
const clamp = (n, a, b) => Math.max(a, Math.min(b, n));
const valid = (s) => {
  if (
    s?.version !== 'bob-bounce-7' ||
    !['ready', 'playing', 'failed', 'complete'].includes(s.status)
  )
    throw new RangeError('Invalid Bob session.');
};
const terminal = (s) => s.status === 'failed' || s.status === 'complete';
function event(s, type, extra = {}) {
  if (extra.points) {
    const category = ['food', 'crate'].includes(type)
      ? 'collection'
      : type === 'bonus-route'
        ? 'routes'
        : type;
    s.earnings[category] = (s.earnings[category] || 0) + extra.points;
  }
  s.events.push({
    id: ++s.eventSerial,
    type,
    x: s.bob.x,
    y: s.bob.y,
    z: s.bob.z,
    points: 0,
    ...extra,
  });
  if (s.events.length > 48) s.events.shift();
  const arcade = rewardBobArcade(s.arcade, s.elapsed, type, extra);
  if (arcade) {
    s.score += arcade.points;
    event(s, 'arcade', {
      x: extra.x ?? s.bob.x,
      y: extra.y ?? s.bob.y,
      z: extra.z ?? s.bob.z,
      ...arcade,
    });
  }
}

/** Create a private practice session. Metres, seconds for velocities, and
 * milliseconds for injected time. Seed identifies a shared course challenge;
 * no local score is a ranked server result. Geometry is separately immutable. */
export function createBobBounce(level = 1, seed = 7) {
  const course = getBobLevel(level);
  if (!Number.isInteger(seed) || seed < 0 || seed > 0xffffffff)
    throw new RangeError('Invalid Bob seed.');
  return {
    version: 'bob-bounce-7',
    arcade: createBobArcade(),
    stumbles: 0,
    checkpoint: null,
    recoveryName: null,
    visitedRoutes: [],
    recoveries: 0,
    assisted: false,
    perfects: 0,
    lastFoodAt: null,
    earnings: {},
    finishBounce: null,
    steering: null,
    smashed: [],
    bonusRoutesCompleted: [],
    level,
    seed,
    status: 'ready',
    elapsed: 0,
    accumulator: 0,
    bob: { ...course.start, vx: 0, vy: 0, vz: 0 },
    score: 0,
    combo: 0,
    bestCombo: 0,
    collected: [],
    bounces: 0,
    eventSerial: 0,
    events: [],
    reason: null,
    timing: { lastGrade: null, lastDelta: null, lastTapAt: null },
    contactCooldowns: {},
    powerupsCollected: [],
    effects: {
      boostUntil: 0,
      stumbleGraceUntil: 0,
      shieldUntil: 0,
      shieldCharges: 0,
      shieldGraceUntil: 0,
      shieldGraceObjectId: null,
    },
    progress: { furthestZ: 0, awardedMetres: 0, timingAwardZ: null, checkpoints: [] },
  };
}
/** Direct flight stroke: descending momentum creates stronger lift near the
 * floor. Apex taps are small impulses; cooldown and a soft ceiling bound lift.
 * Side aim retains inertia; timing points require new forward progress. */
export function tapBobBounce(input, aim = {}) {
  valid(input);
  const x = aim.aimX ?? 0,
    z = aim.aimZ ?? 1;
  if (!Number.isFinite(x) || !Number.isFinite(z)) throw new RangeError('Invalid Bob aim.');
  if (terminal(input) || !bobLandingCue(input).canTap) return input;
  const s = structuredClone(input),
    bob = s.bob;
  const start = s.status === 'ready',
    cue = bobLandingCue(s);
  const fall = clamp(-bob.vy / 8, 0, 1);
  const proximity = clamp(1 - (bob.y - BOB_PHYSICS.radius) / 3.2, 0, 1);
  const power = start ? 0.75 : fall * proximity;
  const grade = start
    ? 'START'
    : cue.window === 'perfect'
      ? 'PERFECT'
      : cue.window === 'good'
        ? 'GOOD'
        : 'EARLY';
  // High taps cannot keep climbing indefinitely. Gravity still acts every step.
  const ceiling = clamp((6.5 - bob.y) / 2, 0, 1);
  const liftLimit = Math.sqrt(Math.max(0, 2 * bobWorldForces(s).gravity * (9.5 - bob.y)));
  bob.vy = start
    ? 6.8
    : clamp(Math.max(0, bob.vy) + (1.1 + 7.1 * power) * ceiling, 0, Math.min(8.8, liftLimit));
  const boost = s.effects.boostUntil > s.elapsed ? 1.2 : 1;
  const drive = (start ? 5.5 : 1.2 + power * 5.5) * boost;
  if (s.steering === null) bob.vx = clamp(bob.vx * 0.45 + clamp(x, -1, 1) * (2 + power * 4), -6, 6);
  bob.vz = clamp(bob.vz * 0.72 + drive * clamp(z, -1, 1), -4, BOB_PHYSICS.maxSpeed);
  s.status = 'playing';
  s.steering = input.steering;
  s.events = [];
  s.timing = {
    lastGrade: grade,
    lastDelta: cue.ms,
    lastTapAt: s.elapsed,
  };
  s.bounces++;
  const progressed = s.progress.timingAwardZ === null || bob.z >= s.progress.timingAwardZ + 1;
  const points = progressed ? (grade === 'PERFECT' ? 100 : grade === 'GOOD' ? 40 : 0) : 0;
  if (points) s.progress.timingAwardZ = bob.z;
  if (points && grade === 'PERFECT') s.perfects++;
  s.score += points;
  event(s, 'timing', { grade, points, power });
  event(s, 'launch', { power });
  return s;
}
// One charge absorbs one physical contact. Grace prevents the same obstacle's
// multiple faces from spending protection twice during its short exit window.
function absorbImpact(s, objectId) {
  if (s.effects.stumbleGraceUntil > s.elapsed) return true;
  if (s.effects.shieldGraceUntil > s.elapsed && s.effects.shieldGraceObjectId === objectId)
    return true;
  if (s.effects.shieldCharges === 0) return false;
  s.effects.shieldCharges = 0;
  s.effects.shieldUntil = 0;
  s.effects.shieldGraceUntil = s.elapsed + 400;
  s.effects.shieldGraceObjectId = objectId;
  event(s, 'shield', { objectId });
  return true;
}
function stumble(s, objectId, reason = 'grounded') {
  if (absorbImpact(s, objectId)) {
    s.bob.y = Math.max(s.bob.y, BOB_PHYSICS.radius);
    s.bob.vy = 5.8;
    return;
  }
  s.stumbles++;
  resetBobArcade(s.arcade);
  s.combo = 0;
  s.lastFoodAt = null;
  const lostGoodies = s.earnings.collection || 0;
  // Consumed pickup IDs remain consumed: losing the stash cannot respawn rewards.
  if (lostGoodies > 0) {
    s.score -= lostGoodies;
    s.earnings.collection = 0;
    s.effects.stumbleGraceUntil = s.elapsed + BOB_PHYSICS.stumbleGraceMs;
  }
  s.bob.y = Math.max(s.bob.y, BOB_PHYSICS.radius);
  s.bob.vy = 5.8;
  s.bob.vx *= 0.5;
  s.bob.vz *= 0.72;
  event(s, 'stumble', { objectId, lostGoodies });
  if (!lostGoodies) fail(s, reason);
}
/** Predict the next descending surface contact; availability uses active time. */
export function bobLandingCue(state) {
  valid(state);
  if (terminal(state) || state.status === 'ready')
    return { ms: null, window: 'ready', armed: false, canTap: state.status === 'ready' };
  const ms = Math.round(bobLanding(state).seconds * 1000);
  return {
    ms,
    window: ms <= 140 ? 'perfect' : ms <= 320 ? 'good' : 'early',
    armed: false,
    canTap: state.elapsed - (state.timing.lastTapAt ?? -Infinity) >= BOB_PHYSICS.tapCooldownMs,
  };
}
/** Continuous air steering in world X, normalized -1..1. Zero brakes sideways;
 * null restores ballistic taps. It cannot add lift, score or forward speed.
 * Reject nonfinite input; terminal rounds are immutable. */
export function steerBobBounce(input, aimX) {
  valid(input);
  if (aimX !== null && !Number.isFinite(aimX)) throw new RangeError('Invalid Bob steering.');
  if (terminal(input)) return input;
  return { ...input, steering: aimX === null ? null : clamp(aimX, -1, 1) };
}
/** Clear transient input feedback without changing momentum, cooldown or score.
 * Direct taps have no queued stroke to cancel on pause, blur or disposal. */
export function clearBobInput(input) {
  valid(input);
  return { ...input, steering: input.steering === null ? null : 0, events: [] };
}
/** Moving obstacle geometry uses the same active clock in policy and renderer. */
export function bobHazardsAt(level, elapsed) {
  const course = getBobLevel(level);
  if (!Number.isFinite(elapsed) || elapsed < 0) throw new RangeError('Invalid Bob time.');
  return course.hazards.map((h) =>
    h.motion
      ? {
          ...h,
          [h.motion.axis]:
            h[h.motion.axis] +
            Math.sin((elapsed / h.motion.periodMs) * Math.PI * 2 + h.motion.phase) *
              h.motion.amplitude,
        }
      : { ...h },
  );
}
function fail(s, reason) {
  s.status = 'failed';
  s.reason = reason;
  event(s, 'fail', { reason });
}
function courseRewards(s, course, previous) {
  if (s.effects.shieldUntil <= s.elapsed) s.effects.shieldCharges = 0;
  for (const item of course.powerups) {
    if (
      s.powerupsCollected.includes(item.id) ||
      Math.hypot(s.bob.x - item.x, s.bob.y - item.y, s.bob.z - item.z) >
        BOB_PHYSICS.radius + item.radius
    )
      continue;
    s.powerupsCollected.push(item.id);
    if (item.kind === 'boost') {
      s.effects.boostUntil = s.elapsed + 6000;
      s.bob.vz = Math.min(BOB_PHYSICS.maxSpeed, Math.max(10, s.bob.vz + 3.5));
    } else {
      s.effects.shieldUntil = s.elapsed + 12000;
      s.effects.shieldCharges = 1;
    }
    event(s, 'powerup', { kind: item.kind, objectId: item.id, x: item.x, y: item.y, z: item.z });
  }
  s.progress.furthestZ = Math.max(s.progress.furthestZ, Math.min(course.length, s.bob.z));
  const metres = Math.floor(s.progress.furthestZ) - s.progress.awardedMetres;
  const speed = Math.max(0, s.bob.vz);
  if (metres >= 5) {
    s.progress.awardedMetres += metres;
    const points = speed >= 4 ? metres * Math.floor(speed / 5) : 0;
    s.score += points;
    if (points) event(s, 'speed', { points, metres });
  }
  for (const checkpoint of course.checkpoints) {
    if (
      s.progress.checkpoints.includes(checkpoint.id) ||
      previous.z >= checkpoint.z ||
      s.bob.z < checkpoint.z
    )
      continue;
    const fraction = (checkpoint.z - previous.z) / (s.bob.z - previous.z);
    const crossingX = previous.x + (s.bob.x - previous.x) * fraction;
    if (
      checkpoint.id !== course.checkpoints[s.progress.checkpoints.length]?.id ||
      Math.abs(crossingX - checkpoint.x) + BOB_PHYSICS.radius > checkpoint.width / 2
    ) {
      fail(s, 'missed-gate');
      return;
    }
    s.progress.checkpoints.push(checkpoint.id);
    const points = 200;
    s.score += points;
    event(s, 'checkpoint', { objectId: checkpoint.id, points });
  }
}
function collectFood(s, food, extra = {}) {
  s.collected.push(food.id);
  if (s.lastFoodAt !== null && s.elapsed - s.lastFoodAt > 3000) s.combo = 0;
  s.lastFoodAt = s.elapsed;
  s.combo++;
  s.bestCombo = Math.max(s.combo, s.bestCombo);
  const points = food.points * Math.min(5, s.combo);
  s.score += points;
  event(s, 'food', {
    foodId: food.id,
    kind: food.kind,
    points,
    combo: s.combo,
    x: food.x,
    y: food.y,
    z: food.z,
    ...extra,
  });
}
function collect(s, course) {
  for (const food of course.food) {
    if (
      s.collected.includes(food.id) ||
      Math.hypot(s.bob.x - food.x, s.bob.y - food.y, s.bob.z - food.z) >
        BOB_PHYSICS.radius + food.radius
    )
      continue;
    collectFood(s, food);
  }
}
function crateReaction(s, course, crate) {
  const chain = bobReactionFood(course, crate, s.collected);
  if (!chain.length) return;
  s.arcade.reactions++;
  event(s, 'chain-reaction', {
    objectId: crate.id,
    count: chain.length,
    radius: BOB_ARCADE.reactionRadius,
    x: crate.x,
    y: crate.y,
    z: crate.z,
    links: chain.map(({ food, from }) => ({ from, to: { x: food.x, y: food.y, z: food.z } })),
  });
  for (const { food } of chain) collectFood(s, food, { reaction: true, sourceObjectId: crate.id });
}
function hitHazard(bob, h) {
  const x = clamp(bob.x, h.x - h.width / 2, h.x + h.width / 2),
    y = clamp(bob.y, h.y - h.height / 2, h.y + h.height / 2),
    z = clamp(bob.z, h.z - h.depth / 2, h.z + h.depth / 2);
  return Math.hypot(bob.x - x, bob.y - y, bob.z - z) < BOB_PHYSICS.radius;
}
function bounceScenery(s, course, previousY) {
  const bob = s.bob;
  for (const h of bobHazardsAt(s.level, s.elapsed)) {
    if (!hitHazard(bob, h)) continue;
    if (h.behavior === 'hazard') {
      stumble(s, h.id, 'hazard');
      if (terminal(s)) return true;
    }
    const faces = [
      ['x', h.x - h.width / 2 - BOB_PHYSICS.radius, -1],
      ['x', h.x + h.width / 2 + BOB_PHYSICS.radius, 1],
      ['y', h.y + h.height / 2 + BOB_PHYSICS.radius, 1],
      ['z', h.z - h.depth / 2 - BOB_PHYSICS.radius, -1],
      ['z', h.z + h.depth / 2 + BOB_PHYSICS.radius, 1],
    ];
    faces.sort((a, b) => Math.abs(bob[a[0]] - a[1]) - Math.abs(bob[b[0]] - b[1]));
    const [axis, position, normal] = faces[0];
    bob[axis] = position + normal * 0.002;
    const key = 'v' + axis;
    if (bob[key] * normal < 0) bob[key] = -bob[key] * 0.72;
    if (axis === 'y') {
      stumble(s, h.id);
      if (terminal(s)) return true;
    }
    if (s.elapsed - (s.contactCooldowns[h.id] ?? -1000) >= 160) {
      event(s, 'bumper', {
        objectId: h.id,
        normal: {
          x: axis === 'x' ? normal : 0,
          y: axis === 'y' ? normal : 0,
          z: axis === 'z' ? normal : 0,
        },
      });
      s.contactCooldowns[h.id] = s.elapsed;
    }
  }
  for (const platform of course.platforms) {
    if (
      platform.y <= 0 ||
      bob.vy > 0 ||
      previousY < platform.y + BOB_PHYSICS.radius ||
      bob.y > platform.y + BOB_PHYSICS.radius ||
      Math.abs(bob.x - platform.x) > platform.width / 2 ||
      Math.abs(bob.z - platform.z) > platform.depth / 2
    )
      continue;
    bob.y = platform.y + BOB_PHYSICS.radius;
    stumble(s, platform.id);
    return terminal(s);
  }
  return false;
}
function bonusContacts(s, course, previousY) {
  const bob = s.bob;
  for (const pad of course.trampolines) {
    const surface = pad.y + BOB_PHYSICS.radius;
    if (
      bob.vy >= 0 ||
      previousY < surface ||
      bob.y > surface ||
      Math.abs(bob.x - pad.x) > pad.width / 2 ||
      Math.abs(bob.z - pad.z) > pad.depth / 2
    )
      continue;
    bob.y = surface;
    bob.vy = 10.5;
    s.effects.springFlightUntilZ = bob.z + 18;
    // Springs preserve a fast entry; the longer arc now requires air steering.
    bob.vz = Math.max(8, bob.vz);
    event(s, 'spring', { objectId: pad.id });
  }
  for (const crate of course.crates) {
    if (
      s.smashed.includes(crate.id) ||
      (crate.kind === 'reinforced' && Math.hypot(bob.vy, bob.vz) < 7) ||
      !hitHazard(bob, { ...crate, width: 1, height: 1, depth: 1 })
    )
      continue;
    s.smashed.push(crate.id);
    if (crate.kind === 'spring') bob.vy = Math.max(bob.vy, 6.8);
    s.score += crate.points;
    event(s, 'crate', {
      objectId: crate.id,
      points: crate.points,
      kind: crate.kind,
      x: crate.x,
      y: crate.y,
      z: crate.z,
    });
    crateReaction(s, course, crate);
  }
  for (const route of course.bonusRoutes) {
    if (
      s.bonusRoutesCompleted.includes(route.id) ||
      !course.crates
        .filter((crate) => crate.routeId === route.id)
        .every((crate) => s.smashed.includes(crate.id))
    )
      continue;
    s.bonusRoutesCompleted.push(route.id);
    s.score += route.reward;
    event(s, 'bonus-route', { objectId: route.id, points: route.reward });
  }
}
function physics(s, course) {
  const dt = BOB_PHYSICS.stepMs / 1000;
  if (s.status === 'ready') return;
  s.elapsed += BOB_PHYSICS.stepMs;
  if (s.arcade.chain && s.arcade.expiresAt <= s.elapsed) resetBobArcade(s.arcade);
  if (s.effects.shieldUntil <= s.elapsed) s.effects.shieldCharges = 0;
  if (s.elapsed >= course.timeLimitMs) {
    fail(s, 'time-limit');
    return;
  }
  const bob = s.bob;
  const world = bobWorldForces(s);
  const gravity = world.gravity;
  const previousPosition = { x: bob.x, z: bob.z };
  const previousY = bob.y;
  if (s.steering !== null)
    bob.vx += (s.steering * 4.5 - bob.vx) * (1 - Math.exp(-world.response * dt));
  bob.x += bob.vx * dt + (course.wind.x * dt * dt) / 2;
  bob.z += bob.vz * dt + (course.wind.z * dt * dt) / 2;
  bob.y += bob.vy * dt - (gravity * dt * dt) / 2;
  bob.vx += course.wind.x * dt;
  bob.vz += course.wind.z * dt;
  bob.vy -= gravity * dt;
  bob.vx += world.x * dt;
  bob.vz = Math.min(BOB_PHYSICS.maxSpeed, bob.vz + world.z * dt);
  bob.vz *= Math.exp(-world.drag * dt);
  // Boost accelerates between taps as well as on pickup, but never teleports
  // position or removes collision response. Expiry uses only the active clock.
  if (s.effects.boostUntil > s.elapsed && bob.vz < 10) bob.vz = Math.min(10, bob.vz + 8 * dt);
  bonusContacts(s, course, previousY);
  collect(s, course);
  courseRewards(s, course, previousPosition);
  if (terminal(s)) return;
  const f = course.finish;
  if (bob.z > f.z + f.radius + 0.2) {
    fail(s, 'overshoot');
    return;
  }
  if (
    bob.x < course.bounds.minX ||
    bob.x > course.bounds.maxX ||
    bob.z < course.bounds.minZ ||
    bob.y < -2
  ) {
    fail(s, 'out-of-bounds');
    return;
  }
  if (bounceScenery(s, course, previousY)) return;
  // Crossing the visible finish line is the goal, not a hidden precision
  // landing. Sweep X at the line so a fast diagonal cannot skip the target.
  const crossedFinish = previousPosition.z < f.z && bob.z >= f.z;
  const crossingX = crossedFinish
    ? previousPosition.x +
      ((bob.x - previousPosition.x) * (f.z - previousPosition.z)) / (bob.z - previousPosition.z)
    : Infinity;
  if (
    crossedFinish &&
    s.progress.checkpoints.length === course.checkpoints.length &&
    Math.abs(crossingX - f.x) <= f.radius
  ) {
    const crossingSeconds = (dt * (f.z - previousPosition.z)) / (bob.z - previousPosition.z);
    const upwardSpeed = bob.vy + gravity * (dt - crossingSeconds);
    const crossingHeight =
      previousY + (bob.vy + gravity * dt) * crossingSeconds - (gravity * crossingSeconds ** 2) / 2;
    s.finishBounce = bobFinishBonus(crossingHeight, upwardSpeed, gravity);
    s.score += s.finishBounce.points;
    event(s, 'finish-bounce', { points: s.finishBounce.points });
    bob.x = crossingX;
    bob.z = f.z;
    bob.vx = 0;
    bob.vy = 0;
    bob.vz = 0;
    s.status = 'complete';
    const points = 1500 + Math.max(0, Math.floor(500 * (1 - s.elapsed / course.parTimeMs)));
    s.score += points;
    event(s, 'complete', { points });
    return;
  }
  if (world.surface > 0 && bob.y <= BOB_PHYSICS.radius + world.surface) {
    bob.y = BOB_PHYSICS.radius + world.surface;
    bob.vy = Math.max(bob.vy, 2.5);
  } else if (bob.y <= BOB_PHYSICS.radius) stumble(s, 'floor');
}

/** Advance 0..100ms using an exact 16ms accumulator. Frame partitioning cannot
 * change fixed-step results. Direct strokes are separate discrete actions;
 * scenery and floor contacts use the same active clock as flight. Finish is
 * a lateral target crossed at any flight speed after every required gate. */
export function stepBobBounce(input, dtMs) {
  valid(input);
  if (!Number.isFinite(dtMs) || dtMs < 0 || dtMs > 100)
    throw new RangeError('Bob steps require 0..100ms.');
  if (terminal(input) || dtMs === 0) return input;
  const state = structuredClone(input),
    course = getBobLevel(state.level);
  state.events = [];
  state.accumulator += dtMs;
  while (state.accumulator >= BOB_PHYSICS.stepMs && !terminal(state)) {
    state.accumulator -= BOB_PHYSICS.stepMs;
    const gates = state.progress.checkpoints.length;
    physics(state, course);
    const lane = course.bonusRoutes.find(
      (r) =>
        !state.visitedRoutes.includes(r.id) &&
        state.bob.z >= r.z - 7 &&
        state.bob.z < r.z &&
        Math.abs(state.bob.x - r.x) < 1.2,
    );
    if (state.status === 'playing' && (state.progress.checkpoints.length > gates || lane)) {
      if (lane) {
        state.visitedRoutes.push(lane.id);
        state.recoveryName = lane.name;
      } else state.recoveryName = `Gate ${state.progress.checkpoints.length}`;
      // Capture every consumable and award atomically, without nested snapshots.
      const { checkpoint: _checkpoint, ...snapshot } = state;
      state.checkpoint = structuredClone(snapshot);
    }
  }
  return state;
}
/** Retry keeps the exact level/seed and never carries points from a failed run. */
export function retryBobBounce(state) {
  valid(state);
  return createBobBounce(state.level, state.seed);
}
/** Explicit Next requires a held success. The final campaign result stays held;
 * callers offer Retry/level selection instead of silently wrapping to level1. */
export function nextBobBounce(state) {
  valid(state);
  if (state.status !== 'complete' || state.level >= 20)
    throw new RangeError('No next Bob level is available.');
  return createBobBounce(state.level + 1, state.seed);
}

/** Explicit checkpoint recovery rolls back both rewards and consumed objects.
 * Keep the serial monotonic for feedback deduplication; assisted runs never set
 * clean records. Without a reached gate, restart the course with a recovery mark. */
export function recoverBobBounce(input) {
  valid(input);
  if (input.status !== 'failed') throw new RangeError('Recovery requires a failed run.');
  const s = input.checkpoint
    ? structuredClone(input.checkpoint)
    : createBobBounce(input.level, input.seed);
  s.checkpoint = input.checkpoint;
  s.recoveries = input.recoveries + 1;
  s.assisted = input.assisted;
  s.status = 'ready';
  s.reason = null;
  s.stumbles = 0;
  s.steering = 0;
  s.accumulator = 0;
  s.events = [];
  s.eventSerial = input.eventSerial + 1;
  s.bob = { ...s.bob, y: Math.max(s.bob.y, 0.85), vx: 0, vy: 0, vz: 0 };
  s.timing.lastTapAt = null;
  return s;
}
